// ts-rest contract for the firm-wide editable settings (ADR-0008).
//
// The legacy Ops Settings UI used the generic `/api/db/:table` CRUD path
// against `settings_kv` to read tolerances and notification toggles. With
// `firm_config` as a typed singleton row, the generic per-session CRUD no
// longer fits: there is one row, it is not session-scoped, and adding a
// new field is a column migration rather than a key insert. This contract
// is the typed read / patch surface the Ops Settings page consumes.
//
// Endpoints under /api/firm-config:
//   GET   /api/firm-config — read the singleton row (returns all-null when
//                            no save has yet happened)
//   PATCH /api/firm-config — patch a subset of fields, bump updated_at
//
// NOTE (PRD #260 / ADR-0013): the notification-toggle columns that previously
// lived on firm_config (`emailCritical`, `emailAttention`, `emailInfo`,
// `slackEnabled`, `dailyDigest`, `weeklyReport`) were the wrong tier and have
// been dropped (migration 0041). Per-user notification preferences are now
// served by `/api/user-prefs/notifications` (see `user-prefs.ts`).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { SESSION_SEAT_KEYS } from '../schemas/session-features';
import {
    staffActionErrorSchema,
    staffActionResponses,
    staffActionResponsesNoNotFound,
} from './agent';

const c = initContract();

// -----------------------------------------------------------------------
// Settings save + User Management endpoints (OLD_CODE.html #2 replacement
// for the `save-tolerances` / `save-notifications` / `add-user` /
// `deactivate-user` named actions in the old registry).
// -----------------------------------------------------------------------

export const tolerancesPatchSchema = z.object({
    positionTolerance: z.string().optional(),
    cashTolerance: z.string().optional(),
    escalationHours: z.string().optional(),
    minCashThreshold: z.string().optional(),
    excessCashThreshold: z.string().optional(),
    settlementAlertDays: z.string().optional(),
});
export type TolerancesPatchDto = z.infer<typeof tolerancesPatchSchema>;

export const addUserRequestSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    role: z.string().optional(),
    personas: z.array(z.string()).min(1),
});
export type AddUserRequest = z.infer<typeof addUserRequestSchema>;

export const updateUserRequestSchema = z.object({
    personas: z.array(z.string()).min(1).optional(),
    role: z.string().optional(),
    /** Acting-for attributions: every IM user id whose Desk book this
     * user works, in switcher order. The list is the WHOLE set — an empty list
     * unassigns (own book), and an absent key leaves the assignments untouched.
     * Server-validated (each IM Active and desk, no chains, no fan-in
     * inversion). */
    actingForUserIds: z.array(z.string()).optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

/**
 * Typed firm-wide settings row. Mirrors the server-side `FirmConfig`
 * shape; every tolerance field is nullable so a freshly-bootstrapped row
 * (all columns null) round-trips.
 *
 * NOTE: notification columns have been removed (migration 0041 / PRD #260).
 */
export const firmConfigSchema = z.object({
    // --- Ops tolerances ---
    positionTolerance: z.string().nullable(),
    cashTolerance: z.string().nullable(),
    escalationHours: z.string().nullable(),
    minCashThreshold: z.string().nullable(),
    excessCashThreshold: z.string().nullable(),
    settlementAlertDays: z.string().nullable(),

    /** ISO 8601 — bumps on every write. */
    updatedAt: z.string(),
});
export type FirmConfigDto = z.infer<typeof firmConfigSchema>;

/**
 * The tolerance subset of the firm-config row — the values the View →
 * Administration Operational Tolerances card reads. Carried on its own
 * admin-gated read (`getTolerances`) so a non-admin cannot fetch the persisted
 * tolerance values; the shared `get` stays `ops`. ADR-0004, issue #209.
 */
export const tolerancesSchema = firmConfigSchema.pick({
    positionTolerance: true,
    cashTolerance: true,
    escalationHours: true,
    minCashThreshold: true,
    excessCashThreshold: true,
    settlementAlertDays: true,
    updatedAt: true,
});
export type TolerancesDto = z.infer<typeof tolerancesSchema>;

/** One per-user AI seat class (ADR-0027). */
export const seatKeySchema = z.enum(SESSION_SEAT_KEYS);
export type SeatKeyDto = z.infer<typeof seatKeySchema>;

/** The state of one seat class in the deployment, for View -> Administration. */
export const seatClassSchema = z.object({
    seat: seatKeySchema,
    /** True when a bought add-on sells the class, or a package grants every seat. */
    bought: z.boolean(),
    /** The most users that can hold the class, or null for no cap. */
    cap: z.number().int().nullable(),
    /** The Active users that hold the class now. */
    used: z.number().int(),
});
export type SeatClassDto = z.infer<typeof seatClassSchema>;

export const seatOverviewSchema = z.object({
    /** True when a bought package (Ultra) gives each user every seat. */
    grantsAllSeats: z.boolean(),
    classes: z.array(seatClassSchema),
});
export type SeatOverviewDto = z.infer<typeof seatOverviewSchema>;

/** A grant or a revoke. 409 is a refusal of the manifest: a cap, an add-on not bought, or Ultra. */
const seatChangeResponses = {
    ...staffActionResponses,
    409: staffActionErrorSchema.extend({
        /** `seats_granted_by_package`, `seat_not_bought` or `seat_cap_reached`. */
        code: z.string(),
    }),
} as const;

export const firmConfigContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/firm-config',
            responses: {
                200: firmConfigSchema,
            },
            summary: 'Read the firm-wide settings singleton row',
        },
        getTolerances: {
            method: 'GET',
            path: '/firm-config/tolerances',
            responses: {
                200: tolerancesSchema,
            },
            summary:
                'Read the Ops tolerance values. Persona-gated to admin (ADR-0004, issue #209) — a non-admin cannot fetch them.',
        },
        saveTolerances: {
            method: 'PATCH',
            path: '/firm-config/tolerances',
            body: tolerancesPatchSchema,
            responses: staffActionResponsesNoNotFound,
            summary: 'Save the Ops tolerances. Persona-gated to ops/admin; audit-stamped.',
        },
        addUser: {
            method: 'POST',
            path: '/firm-config/users',
            body: addUserRequestSchema,
            responses: staffActionResponsesNoNotFound,
            summary:
                'Add a user to the firm-global Entra allowlist (ADR-0004). Persona-gated to admin only.',
        },
        updateUser: {
            method: 'PATCH',
            path: '/firm-config/users/:id',
            pathParams: z.object({ id: z.string() }),
            body: updateUserRequestSchema,
            responses: staffActionResponses,
            summary:
                "Update a user's personas / SMCR role. Persona-gated to admin only (ADR-0004).",
        },
        getSeats: {
            method: 'GET',
            path: '/firm-config/seats',
            responses: { 200: seatOverviewSchema },
            summary:
                'The AI seat classes of the deployment, with caps and use. Persona-gated to admin (ADR-0027).',
        },
        grantSeat: {
            method: 'POST',
            path: '/firm-config/users/:id/seats/:seat',
            pathParams: z.object({ id: z.string(), seat: seatKeySchema }),
            body: z.object({}).optional(),
            responses: seatChangeResponses,
            summary: 'Grant an AI seat to a user. Admin only; audited; the manifest caps apply.',
        },
        revokeSeat: {
            method: 'DELETE',
            path: '/firm-config/users/:id/seats/:seat',
            pathParams: z.object({ id: z.string(), seat: seatKeySchema }),
            body: z.object({}).optional(),
            responses: seatChangeResponses,
            summary: 'Remove an AI seat from a user. Admin only; audited.',
        },
        deactivateUser: {
            method: 'POST',
            path: '/firm-config/users/:id/deactivate',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: staffActionResponses,
            summary: 'Deactivate a user (blocks future Entra logins). Persona-gated to admin only.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
